import React, { useState } from 'react';
import { Download, CheckCircle, BookOpen, Award, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { ebookInfo, founderInfo, contactInfo } from '../data/mock';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { seoMetadata, structuredData } from '../data/seo';

const Ebook = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ebook-subscribers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      toast.success('Thank you! Your ebook is ready to download.');
      setHasDownloaded(true);
      
      // Trigger download
      const link = document.createElement('a');
      link.href = ebookInfo.pdfUrl;
      link.download = 'The-Hidden-Costs-of-Homebuilding.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title={seoMetadata.ebook.title}
        description={seoMetadata.ebook.description}
        keywords={seoMetadata.ebook.keywords}
        canonical={seoMetadata.ebook.canonical}
        additionalStructuredData={structuredData.breadcrumb('Premium Homebuilding Guide', '/ebook')}
      />
      {/* Hero Section with Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${ebookInfo.coverImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/80 to-black/60" />
        
        <div className="relative z-10 container mx-auto px-4 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Left: Ebook Info */}
            <div className="text-white">
              <div className="inline-block px-4 py-2 bg-red-600/90 backdrop-blur-sm rounded-full mb-6">
                <span className="text-white text-sm font-medium">PREMIUM HOMEBUILDING GUIDE</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">
                {ebookInfo.title}
              </h1>
              
              <p className="text-2xl text-red-400 mb-6 font-semibold">
                {ebookInfo.subtitle}
              </p>
              
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                {ebookInfo.description}
              </p>

              <div className="flex items-center space-x-4 mb-8">
                <img 
                  src={founderInfo.image} 
                  alt={founderInfo.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-white"
                />
                <div>
                  <p className="font-semibold text-lg">By {ebookInfo.author}</p>
                  <p className="text-gray-300">{founderInfo.qualification}</p>
                </div>
              </div>

              {/* Quick Benefits */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                    <Download className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium">Instant Download</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium">Expert Content</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-yellow-600 rounded-lg flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium">Save Lakhs</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium">Lifetime Access</span>
                </div>
              </div>
            </div>

            {/* Right: Download Form */}
            <div>
              <Card className="border-0 shadow-2xl bg-white">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    Get Your Premium Homebuilding Guide
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Enter your details to get instant access
                  </p>

                  {!hasDownloaded ? (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          Your Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Enter your phone number"
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter your email"
                          className="w-full"
                        />
                      </div>

                      <div className="flex items-start space-x-2">
                        <input
                          type="checkbox"
                          id="consent"
                          name="consent"
                          required
                          className="mt-1 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                        />
                        <label htmlFor="consent" className="text-sm text-gray-600">
                          I agree that my submitted data is being collected
                        </label>
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
                      >
                        {isSubmitting ? (
                          'Processing...'
                        ) : (
                          <>
                            <Download className="w-5 h-5 mr-2" />
                            Download Premium Homebuilding Guide
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-gray-500 text-center">
                        By downloading, you agree to receive updates from Bombay Home Gallery
                      </p>
                    </form>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-20 h-20 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                        <CheckCircle className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Download Started!</h3>
                      <p className="text-gray-600 mb-6">
                        Your ebook should download automatically. If not, click below.
                      </p>
                      <a href={ebookInfo.pdfUrl} download>
                        <Button className="bg-red-600 hover:bg-red-700 text-white">
                          <Download className="w-5 h-5 mr-2" />
                          Download Again
                        </Button>
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 text-center">
              What's Inside the Ebook?
            </h2>
            <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
              Comprehensive guidance to help you build smarter and save money
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {ebookInfo.features.map((feature, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                      <p className="text-gray-800 font-medium text-lg">{feature}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 text-center">
              Key Benefits
            </h2>

            <div className="space-y-4">
              {ebookInfo.benefits.map((benefit, index) => (
                <Card key={index} className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                        {index + 1}
                      </div>
                      <p className="text-gray-800 text-lg">{benefit}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Perfect For */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12">
              Perfect For
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {ebookInfo.perfectFor.map((audience, index) => (
                <Card key={index} className="border-2 border-gray-200 shadow-lg hover:border-red-600 transition-colors">
                  <CardContent className="p-6 text-center">
                    <Users className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <p className="text-gray-800 font-medium text-lg">{audience}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Build Smarter?
          </h2>
          <p className="text-xl mb-8 text-red-100 max-w-2xl mx-auto">
            Get your Premium Homebuilding Guide now and start making informed decisions for your dream home
          </p>
          
          <a href="#ebook-form" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 shadow-xl">
              <Download className="w-5 h-5 mr-2" />
              Get Your Premium Homebuilding Guide
            </Button>
          </a>

          <div className="mt-8">
            <p className="text-red-100 mb-2">Questions? Contact us:</p>
            <a href={`tel:${contactInfo.phone}`} className="text-white font-semibold hover:underline">
              {contactInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Ebook;
